import React, { useState, useEffect, useMemo, useCallback } from 'react';

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUS_META = {
  Open:    { color: 'var(--red)', bg: 'rgba(224,92,92,0.1)',   border: 'rgba(224,92,92,0.25)'   },
  Partial: { color: 'var(--amber)', bg: 'rgba(212,160,23,0.1)',  border: 'rgba(212,160,23,0.25)'  },
  Closed:  { color: 'var(--green)', bg: 'rgba(76,175,125,0.1)',  border: 'rgba(76,175,125,0.25)'  },
};
const AGE_META = [
  { key: '0-30',  label: '0–30d',  min: 0,  max: 30,   color: 'var(--green)' },
  { key: '31-60', label: '31–60d', min: 31, max: 60,   color: 'var(--amber)' },
  { key: '61-90', label: '61–90d', min: 61, max: 90,   color: 'var(--amber)' },
  { key: '90+',   label: '90+d',   min: 91, max: 99999, color: 'var(--red)' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const API   = process.env.REACT_APP_API_URL || '';
const token = () => localStorage.getItem('bc_token') || '';

function fmtAmt(n, colored = false) {
  if (n === null || n === undefined) return <span style={{ color: 'var(--text3)' }}>—</span>;
  const abs = Math.abs(Number(n));
  const str = '₹' + abs.toLocaleString('en-IN');
  if (!colored) return str;
  if (n > 0) return <span style={{ color: 'var(--green)' }}>{str}</span>;
  if (n < 0) return <span style={{ color: 'var(--red)' }}>-{str}</span>;
  return <span style={{ color: 'var(--text3)' }}>₹0</span>;
}
function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function ageColor(days) {
  if (days > 90) return 'var(--red)';
  if (days > 60) return 'var(--amber)';
  if (days > 30) return 'var(--amber)';
  return 'var(--green)';
}
// Default date range: FY start of current FY → today
function defaultDates() {
  const today = new Date();
  const fyYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  return {
    from: `${fyYear}-04-01`,
    to:   today.toISOString().slice(0, 10),
  };
}
function Spinner() { return <div className="spinner" />; }

// ── URL param helpers ─────────────────────────────────────────────────────────
const PFX = 'd_';
function getDP(k) { return new URLSearchParams(window.location.search).get(PFX + k) || ''; }
function setDP(k, val) {
  const sp = new URLSearchParams(window.location.search);
  const v  = Array.isArray(val) ? val.join('|') : (val || '');
  if (v && v !== 'ALL') sp.set(PFX + k, v); else sp.delete(PFX + k);
  window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}`);
}
function clearDueFilterParams() {
  const sp = new URLSearchParams(window.location.search);
  ['s','st','ag','rc'].forEach(k => sp.delete(PFX + k));
  window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}`);
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function DuePayment() {
  const def = defaultDates();
  const [activeTab,    setActiveTabRaw]    = useState(() => getDP('subtab') || 'outstanding');
  const [fromDate,     setFromDateRaw]     = useState(() => getDP('from') || def.from);
  const [toDate,       setToDateRaw]       = useState(() => getDP('to')   || def.to);

  // Outstanding tab state
  const [outData,      setOutData]      = useState([]);
  const [outLoading,   setOutLoading]   = useState(false);
  const [outError,     setOutError]     = useState(null);
  const [search,       setSearchRaw]       = useState(() => getDP('s') || '');
  const [filterStatus, setFilterStatusRaw] = useState(() => getDP('st') || 'ALL');
  const [filterAge,    setFilterAgeRaw]    = useState(() => getDP('ag') || 'ALL');
  const [filterRecon,  setFilterReconRaw]  = useState(() => getDP('rc') || 'ALL');
  const [sortCol,      setSortCol]      = useState('ActualOutstanding');
  const [sortDir,      setSortDir]      = useState('desc');
  const [expanded,     setExpanded]     = useState(null);

  // Ledger tab state
  const [ledgerData,   setLedgerData]   = useState(null);
  const [ledgerLoading,setLedgerLoading]= useState(false);
  const [ledgerError,  setLedgerError]  = useState(null);

  // Direct setters — update state + URL atomically
  const setActiveTab    = v => { setActiveTabRaw(v);    setDP('subtab', v); };
  const setFromDate     = v => { setFromDateRaw(v);     setDP('from',   v); };
  const setToDate       = v => { setToDateRaw(v);       setDP('to',     v); };
  const setSearch       = v => { setSearchRaw(v);       setDP('s',      v); };
  const setFilterStatus = v => { setFilterStatusRaw(v); setDP('st',     v); };
  const setFilterAge    = v => { setFilterAgeRaw(v);    setDP('ag',     v); };
  const setFilterRecon  = v => { setFilterReconRaw(v);  setDP('rc',     v); };

  // ── Fetch Outstanding ─────────────────────────────────────────────────────
  const fetchOutstanding = useCallback(async () => {
    setOutLoading(true); setOutError(null);
    try {
      const r = await fetch(`${API}/api/due-payment?from=${fromDate}&to=${toDate}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setOutData(j.data || []);
    } catch (e) { setOutError(e.message); }
    finally { setOutLoading(false); }
  }, [fromDate, toDate]);

  // ── Fetch Ledger ──────────────────────────────────────────────────────────
  const fetchLedger = useCallback(async () => {
    setLedgerLoading(true); setLedgerError(null);
    try {
      const r = await fetch(`${API}/api/due-ledger?from=${fromDate}&to=${toDate}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setLedgerData(j);
    } catch (e) { setLedgerError(e.message); }
    finally { setLedgerLoading(false); }
  }, [fromDate, toDate]);

  // Auto-fetch when dates change or tab changes
  useEffect(() => {
    if (activeTab === 'outstanding') fetchOutstanding();
    else if (activeTab === 'ledger') fetchLedger();
  }, [activeTab, fetchOutstanding, fetchLedger]);

  function handleSearch() {
    if (activeTab === 'outstanding') fetchOutstanding();
    else if (activeTab === 'ledger') fetchLedger();
  }

  // ── Outstanding filters ───────────────────────────────────────────────────
  const baseFiltered = useMemo(() => {
    let d = outData;
    if (filterAge !== 'ALL') {
      const ag = AGE_META.find(a => a.key === filterAge);
      if (ag) d = d.filter(r => r.DaysOld >= ag.min && r.DaysOld <= ag.max);
    }
    if (filterRecon !== 'ALL') {
      const rc = Number(filterRecon);
      d = d.filter(r => (r.ReconStatus ?? 0) === rc);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      d = d.filter(r =>
        (r.PartyName || '').toLowerCase().includes(q) ||
        (r.SaleVchNo || '').toLowerCase().includes(q) ||
        (r.Mobile    || '').includes(q)
      );
    }
    return d;
  }, [outData, filterAge, filterRecon, search]);

  const baseForAge = useMemo(() => {
    let d = outData;
    if (filterStatus !== 'ALL') d = d.filter(r => r.DueStatus === filterStatus);
    if (search.trim()) {
      const q = search.toLowerCase();
      d = d.filter(r =>
        (r.PartyName || '').toLowerCase().includes(q) ||
        (r.SaleVchNo || '').toLowerCase().includes(q) ||
        (r.Mobile    || '').includes(q)
      );
    }
    return d;
  }, [outData, filterStatus, search]);

  const statusCounts = useMemo(() => {
    const c = {};
    baseFiltered.forEach(d => { c[d.DueStatus] = (c[d.DueStatus] || 0) + 1; });
    return c;
  }, [baseFiltered]);

  const ageCounts = useMemo(() => {
    const c = {};
    AGE_META.forEach(a => {
      c[a.key] = baseForAge.filter(d => d.DaysOld >= a.min && d.DaysOld <= a.max && d.ActualOutstanding > 0).length;
    });
    return c;
  }, [baseForAge]);

  const stats = useMemo(() => {
    const active = baseFiltered.filter(d => filterStatus === 'ALL' || d.DueStatus === filterStatus);
    const totalOut    = active.reduce((s, d) => s + (d.ActualOutstanding > 0 ? d.ActualOutstanding : 0), 0);
    const totalBilled = active.reduce((s, d) => s + (d.DueBilled || 0), 0);
    const totalSettled= active.reduce((s, d) => s + (d.SettledAmt || 0), 0);
    const overdue     = active.filter(d => d.DaysOld > 30 && d.ActualOutstanding > 0);
    const reconCount  = baseFiltered.filter(d => (d.ReconStatus ?? 0) === 1).length;
    const unreconCount= baseFiltered.filter(d => (d.ReconStatus ?? 0) === 0).length;
    return {
      open:    baseFiltered.filter(d => d.DueStatus === 'Open').length,
      partial: baseFiltered.filter(d => d.DueStatus === 'Partial').length,
      closed:  baseFiltered.filter(d => d.DueStatus === 'Closed').length,
      totalOut, totalBilled, totalSettled,
      overdue: overdue.length,
      overdueAmt: overdue.reduce((s, d) => s + d.ActualOutstanding, 0),
      reconCount, unreconCount,
    };
  }, [baseFiltered, filterStatus]);

  const filtered = useMemo(() => {
    let d = baseFiltered;
    if (filterStatus !== 'ALL') d = d.filter(r => r.DueStatus === filterStatus);
    return [...d].sort((a, b) => {
      let av = a[sortCol] ?? '', bv = b[sortCol] ?? '';
      if (sortCol === 'SaleDate') { av = a.RawDate || ''; bv = b.RawDate || ''; }
      if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }, [baseFiltered, filterStatus, sortCol, sortDir]);

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  }
  function Th({ col, children }) {
    const active = sortCol === col;
    return (
      <th className={active ? 'sorted' : ''} onClick={() => handleSort(col)}>
        {children}<span className="sort-arrow">{active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
      </th>
    );
  }

  // ── Ledger running balance ────────────────────────────────────────────────
  const ledgerRows = useMemo(() => {
    if (!ledgerData) return [];
    let bal = ledgerData.openingBalance || 0;
    const rows = [{ _isOpening: true, balance: bal }];
    for (const e of ledgerData.entries) {
      bal = bal + e.Credit - e.Debit;   // Credit = +, Debit = -
      rows.push({ ...e, computedBal: bal });
    }
    return rows;
  }, [ledgerData]);

  // ── Summary tab ───────────────────────────────────────────────────────────
  const summaryData = useMemo(() => {
    if (!outData.length) return null;
    const ageBuckets = {};
    AGE_META.forEach(a => { ageBuckets[a.key] = { count: 0, amt: 0 }; });
    outData.filter(d => d.ActualOutstanding > 0).forEach(d => {
      const bucket = AGE_META.find(a => d.DaysOld >= a.min && d.DaysOld <= a.max);
      if (bucket) { ageBuckets[bucket.key].count++; ageBuckets[bucket.key].amt += d.ActualOutstanding; }
    });
    return { ageBuckets };
  }, [outData]);

  // ── Date range bar ────────────────────────────────────────────────────────
  function DateBar() {
    const [localFrom, setLocalFrom] = useState(fromDate);
    const [localTo,   setLocalTo]   = useState(toDate);
    function apply() { setFromDate(localFrom); setToDate(localTo); }
    function preset(key) {
      const t = new Date(), d2s = d => d.toISOString().slice(0,10);
      const yr = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear()-1;
      const map = {
        Last30: [d2s(new Date(t-30*864e5)), d2s(t)],
        Last90: [d2s(new Date(t-90*864e5)), d2s(t)],
        FY:     [`${yr}-04-01`, d2s(t)],
        LastFY: [`${yr-1}-04-01`, `${yr}-03-31`],
        AllFY:  ['2022-04-01', d2s(t)],
      };
      if (map[key]) { setFromDate(map[key][0]); setToDate(map[key][1]); }
    }
    return (
      <div className="due-datebar">
        <div className="due-datebar-inputs">
          <input type="date" className="filter-input" value={localFrom}
            onChange={e => setLocalFrom(e.target.value)} style={{fontFamily:'var(--mono)'}} />
          <span style={{color:'var(--text3)',fontSize:13,flexShrink:0}}>to</span>
          <input type="date" className="filter-input" value={localTo}
            onChange={e => setLocalTo(e.target.value)} style={{fontFamily:'var(--mono)'}} />
          <button className="btn-primary" onClick={apply}>Apply</button>
        </div>
        <div className="due-datebar-presets">
                    {[['Last30','30d'],['Last90','90d'],['FY','This FY'],['LastFY','Last FY'],['AllFY','All FY']].map(([k,lbl]) => (
            <button key={k} className="due-preset-btn" onClick={() => preset(k)}>{lbl}</button>
          ))}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="fade-up">
      <div className="page-head">
        <div className="page-title">Due Payments</div>
        <div className="page-sub">DUE PAYMENT ledger (A/C 2068) · multi-year · date range</div>
      </div>

      {/* Sub-tabs */}
      <div className="due-subtabs">
        {[
          { id:'outstanding', label:'📋 Outstanding' },
          { id:'ledger',      label:'📒 Ledger View' },
          { id:'summary',     label:'📊 Summary'     },
        ].map(t => (
          <button key={t.id} className={`due-subtab${activeTab===t.id?' active':''}`}
            onClick={() => setActiveTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* Date bar — always visible */}
      <DateBar />

      {/* ═══ OUTSTANDING TAB ══════════════════════════════════════════════ */}
      {activeTab === 'outstanding' && (
        <>
          {/* Summary Cards */}
          <div className="summary-cards" style={{ marginBottom:12 }}>
            <div className="sum-card">
              <div className="sum-card-label">Net Outstanding</div>
              <div className="sum-card-value red">₹{Math.round(stats.totalOut).toLocaleString('en-IN')}</div>
              <div className="sum-card-sub">open + partial</div>
            </div>
            <div className="sum-card">
              <div className="sum-card-label">Total Billed</div>
              <div className="sum-card-value">₹{Math.round(stats.totalBilled).toLocaleString('en-IN')}</div>
              <div className="sum-card-sub">DUE charged</div>
            </div>
            <div className="sum-card">
              <div className="sum-card-label">Settled</div>
              <div className="sum-card-value" style={{color:'var(--green)'}}>₹{Math.round(stats.totalSettled).toLocaleString('en-IN')}</div>
              <div className="sum-card-sub">received so far</div>
            </div>
            <div className="sum-card">
              <div className="sum-card-label">Open</div>
              <div className="sum-card-value red">{stats.open}</div>
              <div className="sum-card-sub">no payment</div>
            </div>
            <div className="sum-card">
              <div className="sum-card-label">Partial</div>
              <div className="sum-card-value orange">{stats.partial}</div>
              <div className="sum-card-sub">part paid</div>
            </div>
            <div className="sum-card">
              <div className="sum-card-label">Overdue 30+</div>
              <div className="sum-card-value red">{stats.overdue}</div>
              <div className="sum-card-sub">₹{Math.round(stats.overdueAmt).toLocaleString('en-IN')}</div>
            </div>
            <div className="sum-card">
              <div className="sum-card-label">Closed</div>
              <div className="sum-card-value" style={{color:'var(--green)'}}>{stats.closed}</div>
              <div className="sum-card-sub">fully settled</div>
            </div>
          </div>

          {outError && (
            <div className="error-bar"><span className="error-icon">⚠</span>
              <div><div>Error loading data</div><div className="error-detail">{outError}</div></div>
            </div>
          )}

          {/* Filter chips */}
          <div className="stats-row-scroll">
            <button className={`stat-chip ${filterStatus==='ALL'?'active':''}`}
              style={{color:'var(--text2)',borderColor:'var(--border)',background:filterStatus==='ALL'?'var(--surface3)':'transparent'}}
              onClick={() => setFilterStatus('ALL')}>
              <span className="stat-dot" style={{background:'var(--text3)'}} />
              ALL ({baseFiltered.length})
            </button>
            {Object.entries(STATUS_META).map(([key, m]) => (
              <button key={key} className={`stat-chip ${filterStatus===key?'active':''}`}
                style={{color:m.color,background:filterStatus===key?m.bg:'transparent',borderColor:m.border}}
                onClick={() => setFilterStatus(s => s===key?'ALL':key)}>
                <span className="stat-dot" style={{background:m.color}} />
                {key} ({statusCounts[key]||0})
              </button>
            ))}
            <div style={{width:1,height:18,background:'var(--border)',margin:'0 4px'}} />
            {AGE_META.map(ag => (
              <button key={ag.key} className={`stat-chip ${filterAge===ag.key?'active':''}`}
                style={{color:ag.color,background:filterAge===ag.key?`rgba(0,0,0,0.08)`:'transparent',borderColor:`rgba(128,128,128,0.3)`}}
                onClick={() => setFilterAge(k => k===ag.key?'ALL':ag.key)}>
                <span className="stat-dot" style={{background:ag.color}} />
                {ag.label} ({ageCounts[ag.key]||0})
              </button>
            ))}
            <div style={{width:1,height:18,background:'var(--border)',margin:'0 4px'}} />
            {/* ReconStatus filter */}
            <button className={`stat-chip ${filterRecon==='ALL'?'active':''}`}
              style={{color:'var(--text2)',borderColor:'var(--border)',background:filterRecon==='ALL'?'var(--surface3)':'transparent'}}
              onClick={() => setFilterRecon('ALL')}>
              <span className="stat-dot" style={{background:'var(--text3)'}} />
              All Recon
            </button>
            <button className={`stat-chip ${filterRecon==='0'?'active':''}`}
              style={{color:'var(--amber)',background:filterRecon==='0'?'rgba(212,125,63,0.12)':'transparent',borderColor:'rgba(212,125,63,0.3)'}}
              onClick={() => setFilterRecon(r => r==='0'?'ALL':'0')}>
              <span className="stat-dot" style={{background:'var(--amber)'}} />
              Unreconciled ({stats.unreconCount})
            </button>
            <button className={`stat-chip ${filterRecon==='1'?'active':''}`}
              style={{color:'var(--green)',background:filterRecon==='1'?'rgba(76,175,125,0.12)':'transparent',borderColor:'rgba(76,175,125,0.3)'}}
              onClick={() => setFilterRecon(r => r==='1'?'ALL':'1')}>
              <span className="stat-dot" style={{background:'var(--green)'}} />
              Reconciled ({stats.reconCount})
            </button>
          </div>

          {/* Toolbar */}
          <div className="toolbar">
            <div className="toolbar-row-a">
              <div className="search-box">
                <svg className="search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
                <input placeholder="Party, voucher no, mobile…" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <button className={`btn-refresh ${outLoading?'spinning':''}`} onClick={fetchOutstanding} disabled={outLoading}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M1 4v6h6M23 20v-6h-6"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 0 1 3.51 15"/>
                </svg>
                <span>Refresh</span>
              </button>
            </div>
            <div className="toolbar-row-b">
              {(filterStatus !== 'ALL' || filterAge !== 'ALL' || filterRecon !== 'ALL' || search) && (
                <button className="btn-reset" onClick={() => { setFilterStatusRaw('ALL'); setFilterAgeRaw('ALL'); setFilterReconRaw('ALL'); setSearchRaw(''); clearDueFilterParams(); }} disabled={!(filterStatus !== 'ALL' || filterAge !== 'ALL' || filterRecon !== 'ALL' || search)}>↺ Reset</button>
              )}
              <span className="count-badge">{filtered.length} rows</span>
            </div>
          </div>

          {/* Table */}
          {outLoading && !outData.length ? (
            <div className="loading-state"><Spinner /><span>Loading outstanding…</span></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{width:28}}></th>
                    <Th col="DueStatus">Status</Th>
                    <Th col="ReconStatus">Recon</Th>
                    <Th col="FiscalYear">FY</Th>
                    <Th col="SaleVchNo">Voucher</Th>
                    <Th col="SaleDate">Sale Date</Th>
                    <Th col="DaysOld">Age</Th>
                    <Th col="PartyName">Party</Th>
                    <Th col="Mobile">Mobile</Th>
                    <Th col="DueBilled">Due Billed</Th>
                    <Th col="SettledAmt">Settled</Th>
                    <Th col="ActualOutstanding">Outstanding</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={12}>
                      <div className="empty-state">
                        <div className="empty-icon">🔍</div>
                        <h3>No records found</h3>
                        <p>Try changing date range or filters</p>
                      </div>
                    </td></tr>
                  ) : filtered.map((r, i) => {
                    const m = STATUS_META[r.DueStatus] || {};
                    const isExp = expanded === i;
                    return (
                      <React.Fragment key={`${r.SaleVchNo}-${i}`}>
                        <tr style={{cursor:'pointer'}} onClick={() => setExpanded(isExp ? null : i)}>
                          <td style={{textAlign:'center',color:'var(--text3)',fontSize:10}}>{isExp ? '▲' : '▼'}</td>
                          <td>
                            <span className="badge" style={{color:m.color,background:m.bg,borderColor:m.border}}>
                              {r.DueStatus}
                            </span>
                          </td>
                          <td>
                            {r.ReconStatus === 1
                              ? <span style={{fontSize:10,fontWeight:600,color:'var(--green)',background:'rgba(76,175,125,0.1)',border:'1px solid rgba(76,175,125,0.3)',borderRadius:3,padding:'2px 6px'}}>✔ Recon</span>
                              : <span style={{fontSize:10,fontWeight:600,color:'var(--amber)',background:'rgba(212,125,63,0.08)',border:'1px solid rgba(212,125,63,0.25)',borderRadius:3,padding:'2px 6px'}}>Unrecon</span>
                            }
                          </td>
                          <td className="mono dim" style={{fontSize:10,whiteSpace:'nowrap'}}>{r.FiscalYear || '—'}</td>
                          <td className="mono dim" style={{fontSize:11}}>{r.SaleVchNo}</td>
                          <td className="dim" style={{fontSize:11}}>{r.SaleDate}</td>
                          <td className="num">
                            <span style={{color:ageColor(r.DaysOld),fontWeight:600,fontSize:12}}>{r.DaysOld}d</span>
                          </td>
                          <td style={{fontWeight:500}}>{r.PartyName || <span className="muted">—</span>}</td>
                          <td className="mono dim" style={{fontSize:11}}>
                            {r.Mobile
                              ? <a href={`tel:${r.Mobile}`} style={{color:'var(--blue)',textDecoration:'none'}}
                                   onClick={e=>e.stopPropagation()}>{r.Mobile}</a>
                              : <span className="muted">—</span>}
                          </td>
                          <td className="num">₹{Number(r.DueBilled||0).toLocaleString('en-IN')}</td>
                          <td className="num" style={{color:'var(--green)'}}>
                            {r.SettledAmt > 0 ? `₹${Number(r.SettledAmt).toLocaleString('en-IN')}` : <span className="muted">—</span>}
                          </td>
                          <td className="num">
                            <span style={{
                              color: r.ActualOutstanding > 0 ? (r.DaysOld > 30 ? 'var(--red)' : 'var(--amber)') : 'var(--green)',
                              fontWeight: 700,
                            }}>
                              {r.ActualOutstanding > 0 ? `₹${Number(r.ActualOutstanding).toLocaleString('en-IN')}` : '✓ Nil'}
                            </span>
                          </td>
                        </tr>
                        {isExp && (
                          <tr style={{background:'var(--surface2)'}}>
                            <td colSpan={12} style={{padding:'10px 16px 12px 44px'}}>
                              <div style={{display:'flex',gap:28,flexWrap:'wrap',fontSize:12}}>
                                <div>
                                  <div style={{color:'var(--text3)',fontSize:10,letterSpacing:.5,marginBottom:3}}>SETTLEMENT VOUCHERS</div>
                                  <div style={{fontFamily:'var(--mono)',color:'var(--text)',fontSize:11}}>
                                    {!r.SettlementVouchers || r.SettlementVouchers === '-'
                                      ? <span style={{color:'var(--text3)'}}>None recorded</span>
                                      : r.SettlementVouchers}
                                  </div>
                                </div>
                                {r.Mobile && r.ActualOutstanding > 0 && (
                                  <div>
                                    <div style={{color:'var(--text3)',fontSize:10,letterSpacing:.5,marginBottom:3}}>QUICK ACTION</div>
                                    <a href={`https://wa.me/91${(r.Mobile||'').replace(/\D/g,'')}?text=Dear%20${encodeURIComponent(r.PartyName||'')}%2C%20your%20payment%20of%20%E2%82%B9${Math.round(r.ActualOutstanding).toLocaleString('en-IN')}%20against%20bill%20${r.SaleVchNo}%20is%20pending.`}
                                      target="_blank" rel="noreferrer"
                                      style={{display:'inline-flex',alignItems:'center',gap:6,background:'rgba(37,211,102,0.1)',color:'#25d366',border:'1px solid rgba(37,211,102,0.3)',borderRadius:4,padding:'4px 10px',fontSize:11,textDecoration:'none',fontWeight:500}}
                                      onClick={e=>e.stopPropagation()}>
                                      📲 WhatsApp Reminder
                                    </a>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                {filtered.length > 0 && (
                  <tfoot>
                    <tr style={{borderTop:'2px solid var(--border)',fontWeight:700,background:'var(--surface2)'}}>
                      <td colSpan={9} style={{padding:'8px 12px',fontSize:12,color:'var(--text2)'}}>
                        Total ({filtered.length} records)
                      </td>
                      <td className="num" style={{color:'var(--text)'}}>
                        ₹{filtered.reduce((s,r)=>s+(r.DueBilled||0),0).toLocaleString('en-IN')}
                      </td>
                      <td className="num" style={{color:'var(--green)'}}>
                        ₹{filtered.reduce((s,r)=>s+(r.SettledAmt||0),0).toLocaleString('en-IN')}
                      </td>
                      <td className="num" style={{color:'var(--red)'}}>
                        ₹{filtered.filter(r=>r.ActualOutstanding>0).reduce((s,r)=>s+r.ActualOutstanding,0).toLocaleString('en-IN')}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </>
      )}

      {/* ═══ LEDGER TAB ═══════════════════════════════════════════════════ */}
      {activeTab === 'ledger' && (
        <>
          {ledgerError && (
            <div className="error-bar"><span className="error-icon">⚠</span>
              <div><div>Error</div><div className="error-detail">{ledgerError}</div></div>
            </div>
          )}
          <div className="toolbar">
            <div className="toolbar-row-a">
              <span style={{fontSize:12,color:'var(--text2)',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>DUE PAYMENT A/C (2068)</span>
              <button className={`btn-refresh ${ledgerLoading?'spinning':''}`} onClick={fetchLedger} disabled={ledgerLoading}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M1 4v6h6M23 20v-6h-6"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 0 1 3.51 15"/>
                </svg>
                <span>Refresh</span>
              </button>
            </div>
            <div className="toolbar-row-b">
              <span className="count-badge">{(ledgerData?.entries||[]).length} entries</span>
            </div>
          </div>

          {ledgerLoading ? (
            <div className="loading-state"><Spinner /><span>Loading ledger…</span></div>
          ) : !ledgerData ? (
            <div className="loading-state"><span>Apply date range to load ledger</span></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Voucher No</th>
                    <th>Narration</th>
                    <th className="num">Debit (Sale)</th>
                    <th className="num">Credit (Receipt)</th>
                    <th className="num">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerRows.map((row, i) => {
                    if (row._isOpening) return (
                      <tr key="op" style={{background:'var(--surface2)',fontStyle:'italic'}}>
                        <td colSpan={6} style={{color:'var(--text3)',fontSize:11,padding:'7px 12px'}}>Opening Balance (before {fromDate})</td>
                        <td className="num" style={{fontWeight:700}}>
                          {fmtAmt(row.balance, true)}
                        </td>
                      </tr>
                    );
                    const isSale = row.Debit > 0;
                    return (
                      <tr key={i} style={isSale ? {background:'rgba(224,92,92,0.03)'} : {}}>
                        <td className="dim" style={{fontSize:11}}>{row.TxnDate}</td>
                        <td>
                          <span style={{
                            fontSize:10,fontWeight:600,
                            color: isSale ? 'var(--red)' : 'var(--green)',
                            background: isSale ? 'rgba(224,92,92,0.1)' : 'rgba(76,175,125,0.1)',
                            borderRadius:3,padding:'2px 6px',
                          }}>
                            {row.VchType}
                          </span>
                        </td>
                        <td className="mono" style={{fontSize:11}}>{row.VchNo}</td>
                        <td className="dim" style={{fontSize:11,maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {row.Narration || <span style={{color:'var(--text3)'}}>—</span>}
                        </td>
                        <td className="num" style={{color:'var(--red)'}}>
                          {row.Debit > 0 ? `₹${Number(row.Debit).toLocaleString('en-IN')}` : ''}
                        </td>
                        <td className="num" style={{color:'var(--green)'}}>
                          {row.Credit > 0 ? `₹${Number(row.Credit).toLocaleString('en-IN')}` : ''}
                        </td>
                        <td className="num" style={{fontWeight:600}}>
                          {fmtAmt(row.computedBal, true)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {ledgerData?.entries?.length > 0 && (() => {
                  const totalDr = ledgerData.entries.reduce((s,e)=>s+e.Debit,0);
                  const totalCr = ledgerData.entries.reduce((s,e)=>s+e.Credit,0);
                  const closingBal = (ledgerData.openingBalance||0) + totalCr - totalDr;
                  return (
                    <tfoot>
                      <tr style={{borderTop:'2px solid var(--border)',fontWeight:700,background:'var(--surface2)'}}>
                        <td colSpan={4} style={{padding:'8px 12px',fontSize:12,color:'var(--text2)'}}>Period Total</td>
                        <td className="num" style={{color:'var(--red)'}}>₹{totalDr.toLocaleString('en-IN')}</td>
                        <td className="num" style={{color:'var(--green)'}}>₹{totalCr.toLocaleString('en-IN')}</td>
                        <td className="num" style={{fontWeight:700}}>{fmtAmt(closingBal, true)}</td>
                      </tr>
                    </tfoot>
                  );
                })()}
              </table>
            </div>
          )}
        </>
      )}

      {/* ═══ SUMMARY TAB ══════════════════════════════════════════════════ */}
      {activeTab === 'summary' && (
        <>
          {!outData.length ? (
            <div className="loading-state">
              {outLoading ? <><Spinner /><span>Loading…</span></> : <span>Go to Outstanding tab first to load data, then come back here.</span>}
            </div>
          ) : (
            <>
              {/* Top stats */}
              <div className="summary-cards" style={{marginBottom:20}}>
                {Object.entries(STATUS_META).map(([key, m]) => {
                  const rows = outData.filter(d => d.DueStatus === key);
                  const amt = rows.reduce((s,d)=>s+(d.ActualOutstanding>0?d.ActualOutstanding:0),0);
                  return (
                    <div key={key} className="sum-card" style={{borderColor:m.border,cursor:'pointer'}} onClick={()=>{setActiveTab('outstanding');setFilterStatus(key);}}>
                      <div className="sum-card-label">{key}</div>
                      <div className="sum-card-value" style={{color:m.color}}>{rows.length} bills</div>
                      <div className="sum-card-sub">₹{Math.round(amt).toLocaleString('en-IN')} outstanding</div>
                    </div>
                  );
                })}
              </div>

              {/* Aging breakdown */}
              <div style={{marginBottom:20}}>
                <div style={{fontSize:12,fontWeight:700,color:'var(--text2)',marginBottom:10,letterSpacing:.5}}>AGING BREAKDOWN (Open + Partial only)</div>
                {summaryData && AGE_META.map(ag => {
                  const b = summaryData.ageBuckets[ag.key];
                  const totalAmt = AGE_META.reduce((s,a)=>s+summaryData.ageBuckets[a.key].amt,0);
                  const pct = totalAmt > 0 ? (b.amt / totalAmt * 100) : 0;
                  return (
                    <div key={ag.key} style={{marginBottom:10}}>
                      <div style={{display:'flex',justifyContent:'space-between',fontSize:12,marginBottom:4}}>
                        <span style={{color:ag.color,fontWeight:600}}>{ag.label}</span>
                        <span style={{color:'var(--text2)'}}>{b.count} bills · ₹{Math.round(b.amt).toLocaleString('en-IN')}</span>
                      </div>
                      <div style={{height:6,background:'var(--surface2)',borderRadius:3,overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${pct}%`,background:ag.color,borderRadius:3,transition:'width .4s'}} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Top outstanding parties */}
              <div>
                <div style={{fontSize:12,fontWeight:700,color:'var(--text2)',marginBottom:10,letterSpacing:.5}}>TOP 20 OUTSTANDING PARTIES</div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Party</th>
                        <th>Mobile</th>
                        <th className="num">Bills</th>
                        <th className="num">Total Due</th>
                        <th className="num">Settled</th>
                        <th className="num">Outstanding</th>
                        <th className="num">Max Age</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(
                        outData.filter(d=>d.ActualOutstanding>0).reduce((acc, d) => {
                          const key = d.PartyName || 'Unknown';
                          if (!acc[key]) acc[key] = { PartyName:d.PartyName, Mobile:d.Mobile, bills:0, due:0, settled:0, out:0, maxAge:0 };
                          acc[key].bills++;
                          acc[key].due += d.DueBilled || 0;
                          acc[key].settled += d.SettledAmt || 0;
                          acc[key].out += d.ActualOutstanding || 0;
                          acc[key].maxAge = Math.max(acc[key].maxAge, d.DaysOld || 0);
                          return acc;
                        }, {})
                      ).sort((a,b)=>b.out-a.out).slice(0,20).map((p, i) => (
                        <tr key={i}>
                          <td className="dim" style={{fontSize:11}}>{i+1}</td>
                          <td style={{fontWeight:500}}>{p.PartyName || <span className="muted">—</span>}</td>
                          <td className="mono dim" style={{fontSize:11}}>
                            {p.Mobile
                              ? <a href={`tel:${p.Mobile}`} style={{color:'var(--blue)',textDecoration:'none'}}>{p.Mobile}</a>
                              : '—'}
                          </td>
                          <td className="num">{p.bills}</td>
                          <td className="num">₹{Math.round(p.due).toLocaleString('en-IN')}</td>
                          <td className="num" style={{color:'var(--green)'}}>₹{Math.round(p.settled).toLocaleString('en-IN')}</td>
                          <td className="num" style={{color:'var(--red)',fontWeight:700}}>₹{Math.round(p.out).toLocaleString('en-IN')}</td>
                          <td className="num"><span style={{color:ageColor(p.maxAge),fontWeight:600}}>{p.maxAge}d</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
